import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { CheckoutModal } from '../../components/CheckoutModal';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp } from './elementProps';
import { useListing } from './ListingContext';
import { goToListing, isNarrowed, LISTED_FACETS, withoutNarrowing, type ListingAddress } from './listingAddress';
import { useListingWords } from './listingWords';
import { ListingFilterGroups, type FilterChoices } from './ListingFilterGroups';
import './elementListingFilters.css';

// Sleek's filters: a column of folds down the start edge of a collection or search page, and on a phone a
// Filter button opening the same folds in a drawer, with Remove all and a button that shows how many
// products the choices leave. On a page without a listing the element draws nothing.

// Reads which folds the merchant chose to show.
const choicesOf = (props: Record<string, unknown>): FilterChoices => ({
  availability: boolProp(props, 'showAvailability', true), price: boolProp(props, 'showPrice', true),
  options: boolProp(props, 'showOptions', true), types: boolProp(props, 'showTypes', true), vendors: boolProp(props, 'showVendors', false),
  open: boolProp(props, 'startOpen', true)
});

/** Where the folds stand on a screen: a column beside the products, a row of dropdowns above them, or a panel a button slides in. */
const LAYOUTS = ['side', 'bar', 'panel'] as const;

// Counts the choices in use, for the number on the phone's Filter button.
const choicesInUse = (address: ListingAddress): number =>
  (address.available ? 1 : 0) + (address.priceMin !== null || address.priceMax !== null ? 1 : 0) + LISTED_FACETS.reduce((sum, facet) => sum + address[facet].length, 0);

// Draws the filters: the sidebar on a screen, and on a phone a button opening them in a drawer.
export const ListingFiltersElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const listing = useListing();
  const words = useListingWords();
  const { store } = useElementRender();
  const [open, setOpen] = useState(false);
  const facets = listing?.read.data?.facets;
  if (!listing || !facets) return <div {...box} className={[box.className, 'xe-filters'].filter(Boolean).join(' ')} aria-busy={listing?.read.status === 'loading' || undefined} />;
  const unit = (store?.currency ?? 'MAD').toUpperCase() === 'MAD' ? 'DH' : store?.currency ?? '';
  const layout = choiceProp(node.props, 'layout', LAYOUTS, 'side');
  const groups = <ListingFilterGroups facets={facets} address={listing.address} words={words} unit={unit} show={choicesOf(node.props)} />;
  const side = layout === 'bar' ? <ListingFilterGroups facets={facets} address={listing.address} words={words} unit={unit} show={choicesOf(node.props)} floating /> : groups;
  const inUse = choicesInUse(listing.address);
  return (
    <div {...box} className={[box.className, 'xe-filters'].filter(Boolean).join(' ')} data-layout={layout}>
      <button type="button" className="xe-filters-open" aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <SlidersHorizontal size={18} strokeWidth={1.5} aria-hidden="true" /><span>{words.filter}</span>{inUse > 0 ? <span className="xe-filters-used">{inUse}</span> : null}
      </button>
      <div className="xe-filters-side" role="region" aria-label={words.filters}>{side}</div>
      <CheckoutModal open={open} title={words.filters} heading={words.filters} closeLabel={words.close} onClose={() => setOpen(false)} placement={layout === 'panel' ? 'drawer' : 'menu'}>
        <div className="xe-filters-drawer">{groups}</div>
        <div className="xe-filters-drawer-foot">
          <button type="button" className="xe-filters-clear" disabled={!isNarrowed(listing.address)} onClick={() => goToListing(withoutNarrowing(listing.address))}>{words.removeAll}</button>
          <button type="button" className="xe-filters-done" onClick={() => setOpen(false)}>{words.show(listing.read.data?.total ?? 0)}</button>
        </div>
      </CheckoutModal>
    </div>
  );
};
